import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { SavedJob, JobSearchResult } from '@/types/jobSearch';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseRealtimeSubscription, calculateBackoff, RealtimeSubscriptionProps } from '@/utils/supabase/realtimeSubscription';
import { fetchSavedJobs } from '@/services/jobs/fetchSavedJobsService';
import { saveJob as saveSingleJob, unsaveJob as unsaveSingleJob } from '@/services/jobs/manageSavedJobsService';

type RealtimeSubscriptionInstance = ReturnType<typeof useSupabaseRealtimeSubscription>;

/**
 * Custom hook to manage saved jobs with realtime updates
 */
export const useSavedJobs = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [savedJobs, setSavedJobs] = useState<SavedJob[]>([]);
  const [isFetchingSavedJobs, setIsFetchingSavedJobs] = useState(false);
  const [isSavingJobId, setIsSavingJobId] = useState<string | null>(null);
  
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const realtimeSubscriptionObjectRef = useRef<RealtimeSubscriptionInstance | null>(null);

  console.log('[useSavedJobs] Hook initialized. User:', user, 'Auth Loading:', isAuthLoading);

  useEffect(() => {
    console.log('[useSavedJobs] User state changed. New user:', user, 'New Auth Loading:', isAuthLoading);
  }, [user, isAuthLoading]);

  const fetchUserSavedJobs = useCallback(async () => {
    console.log('[useSavedJobs] fetchUserSavedJobs called. Current user:', user);
    if (!user) {
      console.log('[useSavedJobs] fetchUserSavedJobs: No user, setting savedJobs to empty and returning.');
      setSavedJobs([]);
      setIsFetchingSavedJobs(false);
      return;
    }
    
    setIsFetchingSavedJobs(true);
    try {
      const jobs = await fetchSavedJobs(user.id);
      setSavedJobs(jobs);
      retryCountRef.current = 0; 
      console.log('[useSavedJobs] fetchUserSavedJobs: Successfully fetched jobs for user:', user.id, 'Jobs count:', jobs.length);
    } catch (error) {
      console.error('[useSavedJobs] Error in fetchUserSavedJobs:', error);
    } finally {
      setIsFetchingSavedJobs(false);
    }
  }, [user]);

  const handleRealtimeError = useCallback(() => {
    if (!user) { // Don't attempt to retry if there's no user
      console.log('[useSavedJobs] handleRealtimeError: No user, not scheduling reconnection.');
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      return;
    }

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    retryCountRef.current += 1;
    const delay = calculateBackoff(retryCountRef.current);
    
    console.log(`[useSavedJobs] Scheduling realtime reconnection in ${delay}ms (attempt ${retryCountRef.current})`);
    retryTimeoutRef.current = setTimeout(() => {
      if (user && realtimeSubscriptionObjectRef.current) { // Check user again inside timeout
        console.log('[useSavedJobs] Retrying subscription via handleRealtimeError timeout.');
        realtimeSubscriptionObjectRef.current.subscribe();
      }
    }, delay);
  }, [user]); // Depends on user to gate retries

  const realtimeSubscription = useMemo(() => {
    console.log('[useSavedJobs] useMemo: Creating/updating realtimeSubscription object. User ID:', user?.id);
    if (!user?.id) {
      // Return a dummy object that won't cause errors if called when no user
      return {
        subscribe: () => console.log('[useSavedJobs] Memo: Dummy subscribe (no user)'),
        unsubscribe: () => console.log('[useSavedJobs] Memo: Dummy unsubscribe (no user)'),
        channel: null,
        errorCount: 0,
      } as RealtimeSubscriptionInstance; // Cast to the expected type
    }
    return useSupabaseRealtimeSubscription({
      supabase,
      userId: user.id,
      onDataChange: fetchUserSavedJobs,
      onError: handleRealtimeError,
    });
  }, [user?.id, fetchUserSavedJobs, handleRealtimeError]); // supabase is stable

  // Keep the ref updated with the latest memoized subscription object
  useEffect(() => {
    realtimeSubscriptionObjectRef.current = realtimeSubscription;
  }, [realtimeSubscription]);

  useEffect(() => {
    console.log('[useSavedJobs] Main effect triggered. User:', user); // Removed isAuthLoading for brevity, it's logged elsewhere
    
    fetchUserSavedJobs();
    
    if (user) {
      console.log('[useSavedJobs] Main effect: User exists, subscribing to realtime.');
      realtimeSubscription.subscribe();
    } else {
      console.log('[useSavedJobs] Main effect: No user, ensuring unsubscription.');
      realtimeSubscription.unsubscribe(); // Ensure cleanup if user becomes null or on initial load with no user
    }
    
    return () => {
      console.log('[useSavedJobs] Main effect: Cleanup function called.');
      realtimeSubscription.unsubscribe();
      
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      retryCountRef.current = 0; // Reset retry count on cleanup
    };
  }, [user, fetchUserSavedJobs, realtimeSubscription]);

  const saveJob = async (job: JobSearchResult) => {
    console.log('[useSavedJobs] saveJob called for job:', job, 'Current user:', user, 'Auth Loading:', isAuthLoading);
    if (isAuthLoading) {
      console.warn('[useSavedJobs] saveJob: Auth is still loading. Aborting save for now. User:', user);
      return;
    }
    if (!user) {
      console.error('[useSavedJobs] saveJob: No user identified even after auth loading check. Aborting save. User:', user);
      return;
    }
    
    const jobId = job.id;
    setIsSavingJobId(jobId);
    try {
      console.log('[useSavedJobs] saveJob: Calling saveSingleJob for user:', user.id, 'job:', job);
      await saveSingleJob(user.id, job); 
      await fetchUserSavedJobs(); 
      console.log('[useSavedJobs] saveJob: Successfully saved and refreshed jobs');
    } finally {
      setIsSavingJobId(null);
    }
  };

  const unsaveJob = async (jobSearchResultId: string) => {
    console.log('[useSavedJobs] unsaveJob called for jobSearchResultId:', jobSearchResultId, 'Current user:', user, 'Auth Loading:', isAuthLoading);
    if (isAuthLoading) {
      console.warn('[useSavedJobs] unsaveJob: Auth is still loading. Aborting unsave.');
      return;
    }
    if (!user) {
      console.error('[useSavedJobs] unsaveJob: No user identified. Aborting unsave.');
      return;
    }
    
    setIsSavingJobId(jobSearchResultId); 
    try {
      await unsaveSingleJob(user.id, jobSearchResultId);
      setSavedJobs(prev => prev.filter(job => job.job_search_result_id !== jobSearchResultId));
      console.log('[useSavedJobs] unsaveJob: Successfully unsaved jobSearchResultId:', jobSearchResultId);
    } finally {
      setIsSavingJobId(null);
    }
  };

  return {
    savedJobs,
    isFetchingSavedJobs,
    saveJob,
    unsaveJob,
    isSavingJobId,
    fetchSavedJobs: fetchUserSavedJobs, 
  };
};
